"use client"

import { getFixedT } from "@/languages/i18n"
import dayjs from "dayjs"
import Image from "next/image"
import Link from "next/link"
import type { EventItem } from "../types"
import { getEventUrl } from "../utils/utils"

interface EventSlideProps {
  event: EventItem
  locale?: string
}

export const EventSlide = ({ event, locale }: EventSlideProps) => {
  const t = getFixedT(locale)
  const eventUrl = getEventUrl(event.name, event.id)
  const eventDate = event.date ? new Date(event.date) : null

  return (
    <Link href={eventUrl} className="block h-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 h-full p-0 m-0">
        {/* Image Column */}
        <div className="col-span-1 sm:col-span-1 h-auto sm:h-full p-0 m-0">
          {event.coverUrl && (
            <div className="relative w-full h-full min-h-[200px] sm:min-h-0">
              <Image
                src={event.coverUrl}
                alt={event.name}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 100vw, 50vw"
              />
            </div>
          )}
        </div>

        {/* Content Column */}
        <div className="col-span-1 sm:col-span-1 h-auto sm:h-full p-0 m-0 flex flex-col justify-center px-4 sm:px-6 py-6 sm:py-0">
          {/* EVENT Label */}
          <p className="text-[12px] sm:text-sm font-bold text-[#1AAF4D] mb-0">
            {t("EVENT")}
          </p>

          {/* Month, Year */}
          {eventDate && (
            <p className="text-base font-normal mt-[14px] text-[#242f3e] opacity-70 capitalize mb-0">
              {dayjs(eventDate).format("MMM, YYYY")}
            </p>
          )}

          {/* Day */}
          {eventDate && (
            <p className="text-[44px] font-extrabold leading-none mt-[6px] text-[#242F3E] opacity-70 uppercase mb-0">
              {dayjs(eventDate).format("DD")}
            </p>
          )}

          {/* Event Name */}
          <p className="text-lg font-bold mt-[10px] uppercase mb-0">
            {event.name}
          </p>

          {/* Divider */}
          <div className="w-[95%] my-6 border-t border-gray-300" />

          {/* Short Description */}
          {event.shortDescription && (
            <p className="text-base font-normal mt-4 leading-[1.4] mb-0">
              {event.shortDescription}
            </p>
          )}

          {/* Read more */}
          <p className="text-base font-bold mt-1 mb-0">
            {t("Read more", "Read more")}
          </p>
        </div>
      </div>
    </Link>
  )
}
